import React, { useEffect, useMemo, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListItemNode, ListNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { normalizeNotesToLexicalJSON } from '../../lib/richText/lexicalNotes';

interface ReadOnlyNotesRendererProps {
  nodeId: string;
  value: unknown;
}

function normalizeLexicalValue(value: unknown): string {
  return normalizeNotesToLexicalJSON(value);
}

export const ReadOnlyNotesRenderer: React.FC<ReadOnlyNotesRendererProps> = ({ nodeId, value }) => {
  const serialized = useMemo(() => normalizeLexicalValue(value), [value]);

  const config = useMemo(() => ({
    namespace: `notes-readonly-${nodeId}`,
    editable: false,
    editorState: serialized,
    onError: () => {},
    theme: {
      paragraph: 'mb-1.5 text-[13px] leading-relaxed',
      heading: {
        h1: 'text-base font-bold mb-1.5',
        h2: 'text-[15px] font-bold mb-1',
        h3: 'text-[14px] font-semibold mb-1',
      },
      quote: 'border-l-2 border-border pl-3 text-muted-foreground italic text-[13px]',
      list: {
        ul: 'list-disc pl-5 my-1.5 text-[13px]',
        ol: 'list-decimal pl-5 my-1.5 text-[13px]',
        listitem: 'mb-0.5',
      },
      link: 'text-primary underline underline-offset-2 text-[13px]',
      text: {
        bold: 'font-semibold',
        italic: 'italic',
      },
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
  }), [nodeId, serialized]);

  return (
    <LexicalComposer key={nodeId} initialConfig={config}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable className="outline-none text-[13px] text-foreground leading-relaxed" />
        }
        placeholder={null}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <ExternalNotesStateSync serializedState={serialized} />
    </LexicalComposer>
  );
};

const ExternalNotesStateSync: React.FC<{ serializedState: string }> = ({ serializedState }) => {
  const [editor] = useLexicalComposerContext();
  const previousSerializedRef = useRef(serializedState);

  useEffect(() => {
    if (serializedState === previousSerializedRef.current) return;

    const currentState = JSON.stringify(editor.getEditorState().toJSON());
    if (currentState !== serializedState) {
      editor.setEditorState(editor.parseEditorState(serializedState));
    }
    previousSerializedRef.current = serializedState;
  }, [editor, serializedState]);

  return null;
};
